import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { useAiChatActions, useAiChatState } from "./AiChatContext";
import { useAiChat } from "./AiChat.hooks";
import { aiChatResizeHandleColor, useAiChatBorderTreatment, useAiChatStyles } from "./AiChat.styles";
import PaneFrame from "../main/rail/PaneFrame";
import { usePaneSlots } from "../main/panes/usePanes";
import { AiChatAttachment, AiChatSuggestion, AttachmentPreviewTarget } from "./AiChat.types";
import { useTranslation } from "../localization/useTranslation";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import HistoryIcon from "@mui/icons-material/History";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AiChatDocumentViewer from "./documentViewer/AiChatDocumentViewer";
import { AiChatDocumentViewerProvider, useAiChatDocumentViewer } from "./documentViewer/AiChatDocumentViewerContext";
import AiChatSidebar from "./sidebar/AiChatSidebar";
import AiChatInput from "./input/AiChatInput";
import MessageBubble from "./messageBubble/MessageBubble";
import AiSquid from "./assets/aiSquid.svg?react";
import LegalAiLogo from "./assets/legalAiLogo.svg?react";
import WaveHand from "./assets/waveHand.svg?react";
import { aiChatAttachmentsQueryKey } from "./attachments/AiChatAttachment.hooks";
import { useDotLegalQueryClient } from "../common/hooks/dotLegalReactQuery";
import { defaultSuggestions } from "./AiChat.suggestions";

function AiChat() {
    return (
        <AiChatDocumentViewerProvider>
            <AiChatBody />
        </AiChatDocumentViewerProvider>
    );
}

function AiChatBody() {
    const { isOpen, isExpanded, widthState } = useAiChatState();
    const { hasPaneOnStage } = usePaneSlots();
    const { setIsOpen, setIsExpanded, setScope, setPendingPrompt } = useAiChatActions();
    const { messages, isStreaming, conversationId, sendMessage, stopStreaming, startNewChat, loadConversation, ensureConversationId } = useAiChat();
    const { clearViewer, isOpen: isViewerOpen } = useAiChatDocumentViewer();
    const queryClient = useDotLegalQueryClient();
    const { translateString } = useTranslation();
    const styles = useAiChatStyles(isExpanded);
    const borderTreatment = useAiChatBorderTreatment();
    const messageListRef = useRef<HTMLDivElement>(null);
    const [pendingScrollToTopIndex, setPendingScrollToTopIndex] = useState<number | null>(null);
    const [pendingScrollToBottom, setPendingScrollToBottom] = useState(false);
    const [isShowingHistory, setIsShowingHistory] = useState(false);

    const attachmentLookup = new Map<string, AttachmentPreviewTarget>();
    for (const m of messages) {
        for (const att of m.attachments ?? []) {
            attachmentLookup.set(att.id, { id: att.id, fileName: att.fileName });
        }
    }

    useLayoutEffect(() => {
        if (pendingScrollToBottom && messageListRef.current && messages.length > 0) {
            messageListRef.current.scrollTo({ top: messageListRef.current.scrollHeight });
            setPendingScrollToBottom(false);
        }
    }, [pendingScrollToBottom, messages.length]);

    useEffect(() => {
        const shouldLockBodyScroll = isOpen && (isExpanded || isViewerOpen);
        if (!shouldLockBodyScroll) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [isOpen, isExpanded, isViewerOpen]);

    // The chat is also closed from outside (the top bar button, a pane taking the rail), so resetting in
    // our own click handlers would reopen it on the history list. Expanded mode shows the history beside
    // the messages, so collapsing back should land on the messages too.
    useEffect(() => {
        if (!isOpen || isExpanded) {
            setIsShowingHistory(false);
        }
    }, [isOpen, isExpanded]);

    useEffect(() => {
        if (pendingScrollToTopIndex === null) return;
        const list = messageListRef.current;
        const target = list?.querySelector<HTMLElement>(`[data-message-index="${pendingScrollToTopIndex}"]`);
        if (list && target) {
            list.scrollTo({ top: target.offsetTop - list.offsetTop, behavior: "smooth" });
            setPendingScrollToTopIndex(null);
        }
    }, [pendingScrollToTopIndex, messages.length]);

    function handleSend(text: string, readyAttachments: AiChatAttachment[]) {
        const newUserMessageIndex = messages.length;
        sendMessage(text, readyAttachments);
        if (readyAttachments.length > 0) {
            const sentIds = new Set(readyAttachments.map((a) => a.id));
            queryClient.setQueryDataArray<AiChatAttachment[]>([aiChatAttachmentsQueryKey, conversationId], (prev) =>
                (prev ?? []).filter((a) => !sentIds.has(a.id))
            );
        }
        setPendingScrollToTopIndex(newUserMessageIndex);
    }

    function handleSuggestionClick(suggestion: AiChatSuggestion) {
        setScope(conversationId, suggestion.scope);
        setPendingPrompt(translateString(suggestion.promptKey));
    }

    function handleNewChat() {
        setIsShowingHistory(false);
        startNewChat();
        clearViewer();
    }

    async function handleSelectConversation(id: string) {
        setIsShowingHistory(false);
        clearViewer();
        await loadConversation(id);
        setPendingScrollToBottom(true);
    }

    const hasMessages = messages.length > 0;
    const isHistoryShownInWindow = !isExpanded && isShowingHistory;

    return (
        <>
            <AiChatDocumentViewer />
            <PaneFrame
                slot={isOpen ? (isExpanded ? "fullscreen" : "right") : "closed"}
                defaultSlot="right"
                isStageOccupied={hasPaneOnStage}
                widthState={widthState}
                borderTreatment={borderTreatment}
                resizeHandleColor={aiChatResizeHandleColor}
                expandedDirection="row"
            >
                {isOpen && isExpanded && (
                    <Box sx={styles.sidebarColumn}>
                        <AiChatSidebar
                            activeConversationId={conversationId}
                            onSelectConversation={handleSelectConversation}
                            onNewChat={handleNewChat}
                            onActiveConversationDeleted={startNewChat}
                        />
                    </Box>
                )}
                {isOpen && (
                    <Box sx={styles.chatColumn}>
                        <Box sx={styles.header}>
                            {isHistoryShownInWindow && (
                                <DotLegalTooltip text={translateString("goBack")} position="bottom-start">
                                    <IconButton size="small" onClick={() => setIsShowingHistory(false)} sx={styles.headerIconButton}>
                                        <ArrowBackIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </DotLegalTooltip>
                            )}
                            {!isExpanded && !isHistoryShownInWindow && (
                                <Box sx={styles.headerActions}>
                                    <DotLegalTooltip text={translateString("history")} position="bottom-start">
                                        <IconButton size="small" onClick={() => setIsShowingHistory(true)} sx={styles.headerIconButton}>
                                            <HistoryIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </DotLegalTooltip>
                                    {hasMessages && (
                                        <DotLegalTooltip text={translateString("aiChatNewChat")} position="bottom-start">
                                            <IconButton size="small" onClick={handleNewChat} sx={styles.headerIconButton}>
                                                <AddIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </DotLegalTooltip>
                                    )}
                                </Box>
                            )}
                            <Box sx={{ flex: 1 }} />
                            <Box sx={styles.headerActions}>
                                <DotLegalTooltip text={translateString(isExpanded ? "aiChatCollapse" : "aiChatExpand")} position="bottom-end">
                                    <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={styles.headerIconButton}>
                                        {isExpanded ? <CloseFullscreenIcon sx={{ fontSize: 16 }} /> : <OpenInFullIcon sx={{ fontSize: 16 }} />}
                                    </IconButton>
                                </DotLegalTooltip>
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setIsOpen(false);
                                        clearViewer();
                                    }}
                                    sx={styles.headerIconButton}
                                >
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Box>
                        </Box>

                        {isHistoryShownInWindow && (
                            <AiChatSidebar
                                activeConversationId={conversationId}
                                onSelectConversation={handleSelectConversation}
                                onNewChat={handleNewChat}
                                onActiveConversationDeleted={startNewChat}
                            />
                        )}

                        {/* Hidden rather than unmounted, so a half-written message survives a look at the history */}
                        <Box sx={{ display: isHistoryShownInWindow ? "none" : "contents" }}>
                            {!hasMessages ? (
                                <Box sx={styles.welcomeContainer}>
                                    <Box sx={styles.welcomeMascot}>
                                        <AiSquid width={120} height={144} />
                                    </Box>
                                    <Box sx={styles.welcomeBrand}>
                                        <LegalAiLogo />
                                    </Box>
                                    <Typography sx={styles.welcomeTitle}>{translateString("aiComplianceChat")}</Typography>
                                    <Typography sx={styles.welcomeDescription}>
                                        {translateString("aiChatWelcomeDescription")
                                            .split("{wave}")
                                            .flatMap((segment, index, segments) =>
                                                index < segments.length - 1
                                                    ? [segment, <WaveHand key={index} style={{ verticalAlign: "bottom" }} />]
                                                    : [segment]
                                            )}
                                    </Typography>
                                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}>
                                        {defaultSuggestions.map((suggestion) => {
                                            const Icon = suggestion.icon;
                                            return (
                                                <Box
                                                    key={suggestion.labelKey}
                                                    component="button"
                                                    sx={styles.suggestionChip}
                                                    onClick={() => handleSuggestionClick(suggestion)}
                                                >
                                                    <Icon width={20} height={20} style={{ flexShrink: 0 }} />
                                                    <span>{translateString(suggestion.labelKey)}</span>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>
                            ) : (
                                <Box ref={messageListRef} sx={styles.messageList}>
                                    {messages.map((message, index) => {
                                        const hasContent = message.content !== "";
                                        const hasActivity = (message.activity?.length ?? 0) > 0;
                                        const hasAttachments = (message.attachments?.length ?? 0) > 0;
                                        if (!hasContent && !hasActivity && !hasAttachments) return null;
                                        return (
                                            <MessageBubble
                                                key={index}
                                                messageIndex={index}
                                                message={message}
                                                isStreaming={isStreaming && index === messages.length - 1}
                                                styles={styles}
                                                attachmentLookup={attachmentLookup}
                                            />
                                        );
                                    })}
                                    {isStreaming &&
                                        messages[messages.length - 1]?.content === "" &&
                                        (messages[messages.length - 1]?.activity?.length ?? 0) === 0 && (
                                            <Box sx={styles.typingIndicator}>
                                                <span />
                                                <span />
                                                <span />
                                            </Box>
                                        )}
                                </Box>
                            )}

                            <AiChatInput
                                conversationId={conversationId}
                                ensureConversationId={ensureConversationId}
                                isStreaming={isStreaming}
                                hasMessages={hasMessages}
                                styles={styles}
                                onSend={handleSend}
                                onStop={stopStreaming}
                            />
                        </Box>
                    </Box>
                )}
            </PaneFrame>
        </>
    );
}

export default AiChat;
